<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHelpCenter } from '@/composables/useHelpCenter'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { HelpCircle, BookOpen, Maximize2, X, Sparkles } from 'lucide-vue-next'

const route = useRoute()
const { state, open, restore, close, openContextual, getSuggestedGuide, hasContextualHelp } =
  useHelpCenter()

// Verificar si hay ayuda contextual para la ruta actual
const hasContextual = computed(() => hasContextualHelp(route.path))
const suggestedGuide = computed(() => getSuggestedGuide(route.path))

const handleOpen = () => {
  if (hasContextual.value) {
    openContextual(route.path)
  } else {
    open()
  }
}

const tooltipText = computed(() => {
  if (suggestedGuide.value) {
    return `Ayuda: ${suggestedGuide.value.title}`
  }
  return 'Centro de Ayuda'
})
</script>

<template>
  <!-- Botón flotante (cuando no está abierto ni minimizado) -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    leave-active-class="transition-all duration-200 ease-in"
    enter-from-class="opacity-0 scale-75"
    enter-to-class="opacity-100 scale-100"
    leave-from-class="opacity-100 scale-100"
    leave-to-class="opacity-0 scale-75"
  >
    <TooltipProvider v-if="!state.isOpen && !state.isMinimized">
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
            variant="default"
            size="icon"
            class="fixed bottom-4 right-4 z-[80] h-12 w-12 rounded-full shadow-lg group"
            :class="hasContextual ? 'ring-2 ring-primary/50 ring-offset-2 ring-offset-background' : ''"
            @click="handleOpen"
          >
            <span class="relative">
              <HelpCircle class="h-6 w-6" />
              <!-- Indicador de ayuda contextual -->
              <Sparkles
                v-if="hasContextual"
                class="absolute -top-1 -right-1 h-3 w-3 text-yellow-400 animate-pulse"
              />
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left" :side-offset="8">
          <p class="text-sm">{{ tooltipText }}</p>
          <p v-if="hasContextual" class="text-xs text-muted-foreground mt-0.5">
            Click para ver guía contextual
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </Transition>

  <!-- Widget minimizado -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    leave-active-class="transition-all duration-200 ease-in"
    enter-from-class="opacity-0 translate-y-4 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-4 scale-95"
  >
    <div
      v-if="state.isMinimized"
      class="fixed bottom-4 right-80 z-[90] bg-background border rounded-lg shadow-lg p-3 flex items-center gap-3 w-56"
    >
      <div class="flex-shrink-0 h-8 w-8 rounded bg-primary/10 flex items-center justify-center">
        <BookOpen class="h-4 w-4 text-primary" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium truncate">Centro de Ayuda</p>
        <p class="text-xs text-muted-foreground">Minimizado</p>
      </div>
      <div class="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          class="h-7 w-7"
          @click="restore"
          title="Restaurar"
        >
          <Maximize2 class="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          class="h-7 w-7 text-muted-foreground hover:text-destructive"
          @click="close"
          title="Cerrar"
        >
          <X class="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  </Transition>
</template>
